// @ts-check
import api from './apiClient';

const CHUNK_SIZE = 10 * 1024 * 1024; // must match backend MULTIPART_CHUNK_SIZE
const POLL_INTERVAL_MS = 3000;
const MAX_PART_RETRIES = 3;

function resumeKeyFor(file) {
  return `kaay_video_upload:${file.name}:${file.size}:${file.lastModified}`;
}

/** Best-effort — sessionStorage throws in private browsing / when site data is blocked. */
function clearResumeKey(resumeKey) {
  try {
    sessionStorage.removeItem(resumeKey);
  } catch {
    // Non-critical: failing to clear resume state shouldn't surface as an upload failure.
  }
}

async function uploadPart(url, blob) {
  for (let attempt = 1; attempt <= MAX_PART_RETRIES; attempt++) {
    try {
      const res = await fetch(url, { method: 'PUT', body: blob });
      if (!res.ok) throw new Error(`Part upload failed with status ${res.status}`);
      const etag = res.headers.get('ETag');
      if (!etag) throw new Error('R2 did not return an ETag for this part');
      return etag;
    } catch (err) {
      if (attempt === MAX_PART_RETRIES) throw err;
      await new Promise((r) => setTimeout(r, 1000 * attempt));
    }
  }
}

/**
 * Uploads a video via resumable R2 multipart upload, then waits for server-side
 * compression (if the file is over the compression threshold) before resolving with
 * the final CDN URL. Resumes automatically if a prior attempt for the same file
 * (name + size + lastModified) was interrupted by a page refresh or dropped connection.
 *
 * @param {File} file
 * @param {'about_us' | 'showcase'} context
 * @param {(status: {phase: 'uploading'|'compressing', percent?: number}) => void} onProgress
 * @returns {Promise<string>} final CDN URL
 */
export async function uploadVideoResumable(file, context, onProgress) {
  const resumeKey = resumeKeyFor(file);
  let session;
  try {
    session = JSON.parse(sessionStorage.getItem(resumeKey) || 'null');
  } catch {
    session = null;
  }

  if (!session) {
    const init = await api.post('/api/upload/multipart/init', {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
      context,
    });
    session = {
      jobId: init.job_id,
      uploadId: init.upload_id,
      chunkSize: init.chunk_size || CHUNK_SIZE,
      totalParts: init.total_parts,
    };
    try {
      sessionStorage.setItem(resumeKey, JSON.stringify(session));
    } catch {
      // Non-critical: resume bookkeeping failing (private browsing, storage quota) should
      // never block the upload itself — we just lose the ability to resume on refresh.
    }
  }

  let existingParts;
  try {
    existingParts = await api.get(`/api/upload/multipart/${session.jobId}/parts`);
  } catch {
    // The stored session points at a job that can no longer accept parts (failed, already
    // completed, or its R2 upload is gone). Drop it so the next attempt starts a fresh
    // job instead of retrying the same dead session forever.
    clearResumeKey(resumeKey);
    throw new Error('Upload session expired or failed — please try uploading again');
  }
  const uploaded = new Map(existingParts.map((p) => [p.part_number, p.etag]));

  for (let partNumber = 1; partNumber <= session.totalParts; partNumber++) {
    if (uploaded.has(partNumber)) continue;
    const start = (partNumber - 1) * session.chunkSize;
    const blob = file.slice(start, start + session.chunkSize);
    const urlResp = await api.get(`/api/upload/multipart/${session.jobId}/part-urls?part_numbers=${partNumber}`);
    const etag = await uploadPart(urlResp.urls[partNumber], blob);
    uploaded.set(partNumber, etag);
    onProgress({ phase: 'uploading', percent: Math.round((uploaded.size / session.totalParts) * 100) });
  }

  const parts = Array.from(uploaded.entries()).map(([part_number, etag]) => ({ part_number, etag }));
  let status = await api.post(`/api/upload/multipart/${session.jobId}/complete`, { parts });

  while (status.status === 'queued' || status.status === 'processing') {
    onProgress({ phase: 'compressing' });
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    status = await api.get(`/api/upload/jobs/${session.jobId}/status`);
  }

  clearResumeKey(resumeKey);

  if (status.status === 'failed') {
    throw new Error(status.error || 'Video processing failed');
  }
  return status.url;
}
